import React, { useState } from 'react';
import { PLAYER, CREW, IMAGES, PROFILE_ASSETS, ACHIEVEMENTS, PROFILE_HISTORY } from '../mock/data';
import { useDemo } from '../context/DemoContext';
import { Reveal, Eyebrow } from '../components/xc/Bits';
import { Coins, Wallet, Shield, Trophy, Swords, Target, Zap, Check, Lock, Car, Home, Shirt, Star, Gem } from 'lucide-react';

const TABS = ['Overview', 'Inventory', 'Garage', 'Property', 'Achievements', 'History'];
const rarityColor = { Legendary: '#E0B85A', Epic: '#B026FF', Rare: '#FF204E', 'Mission Unlock': '#2FBF71' };

function AssetCard({ a, i }) {
  return (
    <Reveal delay={i * 50}>
      <div className="xc-card rounded-2xl overflow-hidden">
        <div className="relative h-40">
          <img src={a.img} alt={a.name} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0D0B10] to-transparent" />
          <span className="absolute top-3 left-3 text-[10px] font-600 tracking-[0.12em] uppercase px-2.5 py-1 rounded-full" style={{ color: rarityColor[a.rarity], border: `1px solid ${rarityColor[a.rarity]}55`, background: `${rarityColor[a.rarity]}14` }}>{a.rarity}</span>
        </div>
        <div className="p-4"><div className="font-display font-600 text-white">{a.name}</div><div className="text-xs text-white/45 mt-0.5">{a.type}</div></div>
      </div>
    </Reveal>
  );
}

export default function Profile() {
  const { loyaltyCoins, demoBalance } = useDemo();
  const [tab, setTab] = useState('Overview');
  const xpPct = Math.round((PLAYER.xp / PLAYER.xpNext) * 100);
  const filterAssets = (types) => PROFILE_ASSETS.filter((a) => types.includes(a.type));

  return (
    <div className="bg-[#070609] min-h-screen">
      {/* Hero */}
      <section className="relative h-[42vh] min-h-[320px] overflow-hidden">
        <img src={IMAGES.penthouse} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg,rgba(7,6,9,0.4),rgba(7,6,9,0.6),#070609)' }} />
        <div className="absolute inset-0 vignette" />
        <div className="relative max-w-[1440px] mx-auto px-4 lg:px-8 h-full flex items-end pb-8">
          <div className="flex items-end gap-5">
            <div className="h-24 w-24 rounded-2xl grid place-items-center flex-shrink-0 text-4xl font-display font-700 text-white" style={{ background: 'linear-gradient(135deg,#D90035,#7A18FF)', boxShadow: '0 0 40px -8px rgba(176,38,255,0.7)' }}>{PLAYER.avatarInitial}</div>
            <div>
              <div className="flex items-center gap-2 mb-1"><Shield className="h-4 w-4 text-[#E0B85A]" /><span className="text-sm text-white/70">{CREW.name} · Rep {CREW.reputation.toLocaleString()}</span></div>
              <h1 className="font-display font-700 text-4xl md:text-5xl text-white tracking-tight">{PLAYER.name.toUpperCase()}</h1>
              <div className="mt-1 text-[#E0B85A] font-600">Level {PLAYER.level}</div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-4 lg:px-8 py-8">
        {/* XP + balances */}
        <div className="grid lg:grid-cols-[1fr_300px] gap-4">
          <div className="xc-glass rounded-2xl p-6">
            <div className="flex items-center justify-between mb-2 text-sm"><span className="text-white/60">Experience</span><span className="text-white/80">{PLAYER.xp.toLocaleString()} / {PLAYER.xpNext.toLocaleString()} XP</span></div>
            <div className="h-3 rounded-full bg-white/10 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${xpPct}%`, background: 'linear-gradient(90deg,#D90035,#B026FF)' }} /></div>
            <div className="mt-2 text-xs text-white/40">{PLAYER.xpNext - PLAYER.xp} XP to Level {PLAYER.level + 1}</div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="xc-glass rounded-2xl p-5"><Coins className="h-5 w-5 text-[#E0B85A] mb-1" /><div className="text-[11px] text-white/40 uppercase">Loyalty Coins</div><div className="font-display font-700 text-lg text-[#E0B85A]">{loyaltyCoins.toLocaleString()}</div></div>
            <div className="xc-glass rounded-2xl p-5"><Wallet className="h-5 w-5 text-white/70 mb-1" /><div className="text-[11px] text-white/40 uppercase">Demo Balance</div><div className="font-display font-700 text-lg text-white">€{demoBalance.toFixed(2)}</div></div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          {[{ i: Target, l: 'Missions Completed', v: PLAYER.stats.missionsCompleted, c: '#FF204E' }, { i: Trophy, l: 'Crew Victories', v: PLAYER.stats.crewVictories, c: '#E0B85A' }, { i: Swords, l: 'PvP Wins', v: PLAYER.stats.pvpWins, c: '#B026FF' }, { i: Zap, l: 'Qualifying Rounds', v: PLAYER.stats.qualifyingRounds.toLocaleString(), c: '#B026FF' }].map((s, k) => (
            <div key={k} className="xc-card rounded-xl p-5"><s.i className="h-5 w-5 mb-2" style={{ color: s.c }} /><div className="font-display font-700 text-2xl text-white">{s.v}</div><div className="text-xs text-white/45 mt-0.5">{s.l}</div></div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mt-8 mb-6 border-b border-white/8 overflow-x-auto no-scrollbar">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`px-4 py-3 text-sm font-500 relative whitespace-nowrap transition-colors ${tab === t ? 'text-white' : 'text-white/45 hover:text-white/75'}`}>{t}{tab === t && <span className="absolute bottom-0 left-0 right-0 h-0.5" style={{ background: 'linear-gradient(90deg,#FF204E,#B026FF)' }} />}</button>
          ))}
        </div>

        {tab === 'Overview' && (
          <div>
            <Eyebrow color="#E0B85A">Featured Assets</Eyebrow>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-3">{PROFILE_ASSETS.map((a, i) => <AssetCard key={a.name} a={a} i={i} />)}</div>
          </div>
        )}
        {tab === 'Inventory' && <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{PROFILE_ASSETS.map((a, i) => <AssetCard key={a.name} a={a} i={i} />)}</div>}
        {tab === 'Garage' && <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{filterAssets(['Vehicle']).map((a, i) => <AssetCard key={a.name} a={a} i={i} />)}</div>}
        {tab === 'Property' && <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{filterAssets(['Property']).map((a, i) => <AssetCard key={a.name} a={a} i={i} />)}</div>}

        {tab === 'Achievements' && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ACHIEVEMENTS.map((a, i) => (
              <Reveal key={a.name} delay={i * 40}>
                <div className={`xc-card rounded-xl p-5 flex items-start gap-3 ${!a.done ? 'opacity-60' : ''}`}>
                  <div className="h-10 w-10 rounded-lg grid place-items-center flex-shrink-0" style={{ background: a.done ? '#E0B85A18' : 'rgba(255,255,255,0.04)', border: `1px solid ${a.done ? '#E0B85A55' : 'rgba(255,255,255,0.1)'}` }}>{a.done ? <Check className="h-5 w-5 text-[#E0B85A]" /> : <Lock className="h-5 w-5 text-white/40" />}</div>
                  <div><div className="font-500 text-white">{a.name}</div><div className="text-sm text-white/45 mt-0.5">{a.desc}</div></div>
                </div>
              </Reveal>
            ))}
          </div>
        )}

        {tab === 'History' && (
          <div className="max-w-2xl xc-glass rounded-2xl overflow-hidden">
            {PROFILE_HISTORY.map((h, i) => (
              <div key={i} className="flex items-center justify-between px-5 py-4 border-b border-white/5 last:border-0">
                <div><div className="font-500 text-white">{h.title}</div><div className="text-xs text-white/40 mt-0.5">{h.when}</div></div>
                <div className="text-right"><div className="text-sm font-500" style={{ color: h.result === 'Failed' ? '#FF204E' : '#2FBF71' }}>{h.result}</div><div className="text-xs text-[#E0B85A] mt-0.5">{h.reward}</div></div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
